import styled from 'styled-components';
import type { Asset } from '../../types/ugla-types';
import OptionItem from './OptionItem';
import { getCategoryIcon } from './CategoryItem';

interface OptionsPanelProps {
  category: string;
  optionKey?: string;
  options: string[];
  selectedOption: string | undefined;
  onOptionSelect: (option: string) => void;
  getAssetForOption: (option: string) => Asset | undefined;
  mode?: 'refs' | 'option-values';
}

/**
 * Options panel component - displays product references for selected category
 * Shows available options (product_ref values) for the currently selected category (product_layer)
 */
const OptionsPanel: React.FC<OptionsPanelProps> = ({
  category,
  optionKey,
  options,
  selectedOption,
  onOptionSelect,
  getAssetForOption,
  mode = 'refs',
}) => {
  const panelTitle = mode === 'option-values' && optionKey ? optionKey : category;

  if (options.length === 0) {
    return (
      <Container>
        <Title>
          {panelTitle.charAt(0).toUpperCase() + panelTitle.slice(1)} Options
        </Title>
        <EmptyMessage>No options available for this category</EmptyMessage>
      </Container>
    );
  }
  
  const CategoryIcon = getCategoryIcon(category);

  return (
    <Container>
      <OptionIcon>
        <IconTitle>
          {panelTitle}
        </IconTitle>
        <IconContainer>
          <CategoryIcon width={40} height={40} fill="white"/>
        </IconContainer>
      </OptionIcon>
      <OptionsList>
        {options.map((option) => {
          const asset = getAssetForOption(option);
          const label =
            mode === 'option-values'
              ? option
              : asset?.tags?.find((tag) => tag.key === 'product_name')?.value ?? option;

          return (
            <OptionItem
              key={option}
              option={label}
              asset={asset}
              selected={selectedOption === option}
              onClick={() => onOptionSelect(option)}
            />
          );
        })}
      </OptionsList>
    </Container>
  );
};

const Container = styled.div`
  padding: .5vh 1vw;
  height: 100%;
  display: flex;
  flex-direction: row;
  overflow: hidden;
`;

const OptionIcon = styled.div`
  display: flex;
  flex-direction: column;
  color: white;
  align-content: center;
  justify-content: center;
  margin-right: 1vw;
  gap: .5rem;
  padding: 1vh 1vw;
`

const IconTitle = styled.div`
  font-family: Poppins-Medium, sans-serif;
  font-size: 20px;
  text-align: center;
`

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
`

const Title = styled.h3`
  color: #ffffff;
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-transform: capitalize;
  letter-spacing: 0.02em;
`;

const OptionsList = styled.div`
  display: flex;
  gap: 1rem;
  padding-top: 4px;
  flex: 1;
  align-items: center;
  overflow-x: auto;
  overflow-y: hidden;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 5px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 5px;

    &:hover {
      background: rgba(255, 255, 255, 0.5);
    }
  }

  /* Smooth scrolling */
  scroll-behavior: smooth;
`;

const EmptyMessage = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  text-align: center;
  padding: 2rem 1rem;
  font-style: italic;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default OptionsPanel;
